import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { PageTitle } from '../../../../../_metronic/layout/core'
import Swal from 'sweetalert2'
import { ReportShell } from '../components/ReportShell'
import { SellerIdentity } from '../components/SellerIdentity'
import { createDefaultSellerReportDateRange, useSellerReportFilters } from '../useSellerReportFilters'
import { loadSellerTicketAverageReport } from '../services'
import {
  SellerReportSourceFilter,
  SellerTicketAverageFilters,
  SellerTicketAverageReport,
} from '../types'
import { ConferenceOrderItem, ConferenceOrderSource } from '../../SellerQueue/types'
import * as XLSX from 'xlsx'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'

type DependencySource = ConferenceOrderSource | 'balanced'
type DependencyLevel = 'critical' | 'high' | 'moderate' | 'balanced'

interface DependencyRow {
  sellerKey: string
  sellerLabel: string
  totalOrders: number
  totalAmountCents: number
  ninegridAmountCents: number
  totvsAmountCents: number
  ninegridSharePercent: number
  totvsSharePercent: number
  dependencySource: DependencySource
  dependencyLevel: DependencyLevel
}

const currencyFormatter = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
})

const percentFormatter = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
})

const formatCurrency = (amountCents: number): string => currencyFormatter.format(amountCents / 100)

const isRecord = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value)

const readString = (value: unknown): string => {
  if (typeof value === 'string' && value.trim()) {
    return value.trim()
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value)
  }

  return ''
}

const getSellerKeyAndLabel = (order: ConferenceOrderItem): { sellerKey: string; sellerLabel: string } => {
  const content = isRecord(order.content) ? order.content : {}
  const rawTotvsOrder = isRecord(content.rawTotvsOrder) ? content.rawTotvsOrder : {}
  const rawNinegridOrder = isRecord(content.rawNinegridOrder) ? content.rawNinegridOrder : {}
  const commissioneds = Array.isArray(rawTotvsOrder.commissioneds) ? rawTotvsOrder.commissioneds : []
  const firstCommissioned = isRecord(commissioneds[0]) ? commissioneds[0] : {}

  const sellerNameCandidates = [
    content.sellerName,
    rawNinegridOrder.sellerName,
    rawTotvsOrder.sellerName,
    firstCommissioned.sellerName,
    rawTotvsOrder.representativeName,
  ]

  const sellerCodeCandidates = [
    content.sellerCode,
    rawNinegridOrder.sellerCode,
    rawTotvsOrder.sellerCode,
    firstCommissioned.sellerCode,
    rawTotvsOrder.representativeCode,
  ]

  const sellerCpfCandidates = [
    content.sellerCpf,
    rawNinegridOrder.sellerCpf,
    rawTotvsOrder.sellerCpfCnpj,
    firstCommissioned.sellerCpfCnpj,
  ]

  const sellerName = sellerNameCandidates.map(readString).find(Boolean) || ''
  const sellerCode = sellerCodeCandidates.map(readString).find(Boolean) || ''
  const sellerCpf = sellerCpfCandidates.map(readString).find(Boolean) || ''

  const sellerLabel = sellerName || sellerCode || sellerCpf || 'Sem vendedor'
  const sellerKey = sellerCode || sellerCpf || sellerName || 'sem-vendedor'

  return {
    sellerKey,
    sellerLabel,
  }
}

const resolveDependencyLevel = (highestSharePercent: number): DependencyLevel => {
  if (highestSharePercent >= 95) {
    return 'critical'
  }

  if (highestSharePercent >= 85) {
    return 'high'
  }

  if (highestSharePercent >= 70) {
    return 'moderate'
  }

  return 'balanced'
}

const buildDependencyRows = (report: SellerTicketAverageReport | null): DependencyRow[] => {
  if (!report?.items.length) {
    return []
  }

  const enrichedLabelBySellerKey = new Map(report.rows.map((row) => [row.sellerKey, row.sellerLabel]))
  const grouped = new Map<string, Omit<DependencyRow, 'ninegridSharePercent' | 'totvsSharePercent' | 'dependencySource' | 'dependencyLevel'>>()

  for (const order of report.items) {
    const source = order.source
    if (source !== 'ninegrid' && source !== 'totvs') {
      continue
    }

    const { sellerKey, sellerLabel } = getSellerKeyAndLabel(order)
    const current = grouped.get(sellerKey) || {
      sellerKey,
      sellerLabel: enrichedLabelBySellerKey.get(sellerKey) || sellerLabel,
      totalOrders: 0,
      totalAmountCents: 0,
      ninegridAmountCents: 0,
      totvsAmountCents: 0,
    }

    const orderTotal = typeof order.total === 'number' ? Math.round(order.total) : 0

    current.sellerLabel = enrichedLabelBySellerKey.get(sellerKey) || sellerLabel || current.sellerLabel
    current.totalOrders += 1
    current.totalAmountCents += orderTotal

    if (source === 'ninegrid') {
      current.ninegridAmountCents += orderTotal
    } else {
      current.totvsAmountCents += orderTotal
    }

    grouped.set(sellerKey, current)
  }

  return Array.from(grouped.values())
    .map((row) => {
      const ninegridSharePercent =
        row.totalAmountCents > 0 ? (row.ninegridAmountCents / row.totalAmountCents) * 100 : 0
      const totvsSharePercent =
        row.totalAmountCents > 0 ? (row.totvsAmountCents / row.totalAmountCents) * 100 : 0

      let dependencySource: DependencySource = 'balanced'
      if (ninegridSharePercent > totvsSharePercent) {
        dependencySource = 'ninegrid'
      } else if (totvsSharePercent > ninegridSharePercent) {
        dependencySource = 'totvs'
      }

      const dependencyLevel = resolveDependencyLevel(Math.max(ninegridSharePercent, totvsSharePercent))

      return {
        ...row,
        ninegridSharePercent,
        totvsSharePercent,
        dependencySource,
        dependencyLevel,
      }
    })
    .sort((left, right) => {
      const leftValue = Math.max(left.ninegridSharePercent, left.totvsSharePercent)
      const rightValue = Math.max(right.ninegridSharePercent, right.totvsSharePercent)
      return rightValue - leftValue || right.totalAmountCents - left.totalAmountCents
    })
}

const dependencyLabel = (source: DependencySource): string => {
  if (source === 'ninegrid') {
    return 'Dependente de Ninegrid'
  }

  if (source === 'totvs') {
    return 'Dependente de TOTVS'
  }

  return 'Equilibrado'
}

const dependencyBadgeClass = (level: DependencyLevel): string => {
  if (level === 'critical') {
    return 'badge badge-light-danger'
  }

  if (level === 'high') {
    return 'badge badge-light-warning'
  }

  if (level === 'moderate') {
    return 'badge badge-light-info'
  }

  return 'badge badge-light-success'
}

const dependencyLevelLabel = (level: DependencyLevel): string => {
  if (level === 'critical') {
    return 'Critica'
  }

  if (level === 'high') {
    return 'Alta'
  }

  if (level === 'moderate') {
    return 'Moderada'
  }

  return 'Balanceada'
}

const exportToCSV = (filename: string, rows: DependencyRow[]): void => {
  let csv = 'sep=;\n'
  csv += 'Posição;Vendedor;Pedidos;Faturamento;Ninegrid (%);TOTVS (%);Canal dominante;Nivel;Risco\n'

  rows.forEach((row, index) => {
    csv += `${index + 1};"${row.sellerLabel}";${row.totalOrders};${row.totalAmountCents / 100};${row.ninegridSharePercent.toFixed(1)};${row.totvsSharePercent.toFixed(1)};"${dependencyLabel(row.dependencySource)}";${dependencyLevelLabel(row.dependencyLevel)};${row.dependencyLevel}\n`
  })

  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' })
  const link = document.createElement('a')
  const url = URL.createObjectURL(blob)
  link.setAttribute('href', url)
  link.setAttribute('download', filename)
  link.style.visibility = 'hidden'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

const exportToXLSX = (filename: string, rows: DependencyRow[]): void => {
  const data = rows.map((row, index) => ({
    Posição: index + 1,
    Vendedor: row.sellerLabel,
    Pedidos: row.totalOrders,
    Faturamento: row.totalAmountCents / 100,
    'Ninegrid (%)': row.ninegridSharePercent.toFixed(1),
    'TOTVS (%)': row.totvsSharePercent.toFixed(1),
    'Canal dominante': dependencyLabel(row.dependencySource),
    Nível: dependencyLevelLabel(row.dependencyLevel),
  }))

  const worksheet = XLSX.utils.json_to_sheet(data)
  const workbook = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Dependencia')
  XLSX.writeFile(workbook, filename)
}

const exportToPDF = (filename: string, rows: DependencyRow[], report: SellerTicketAverageReport | null): void => {
  const pdf = new jsPDF()
  const pageWidth = pdf.internal.pageSize.getWidth()
  const pageHeight = pdf.internal.pageSize.getHeight()
  const margin = 15
  let yPosition = margin

  pdf.setFontSize(16)
  pdf.text('Relatório de Dependência de Canal', margin, yPosition)
  yPosition += 8

  pdf.setFontSize(10)
  pdf.setTextColor(100, 100, 100)
  pdf.text(`Relatório gerado em: ${new Date().toLocaleString('pt-BR')}`, margin, yPosition)
  yPosition += 12

  pdf.setFontSize(11)
  pdf.setTextColor(0, 0, 0)
  pdf.text('Resumo', margin, yPosition)
  yPosition += 6

  pdf.setFontSize(9)
  pdf.text(`Vendedores: ${rows.length} | Receita total: ${formatCurrency(report?.summary.totalAmountCents || 0)}`, margin, yPosition)
  yPosition += 10

  autoTable(pdf, {
    startY: yPosition,
    head: [['Pos', 'Vendedor', 'Pedidos', 'Faturamento', 'Ninegrid (%)', 'TOTVS (%)', 'Canal', 'Nivel']],
    body: rows.map((row, index) => [
      index + 1,
      row.sellerLabel,
      row.totalOrders,
      formatCurrency(row.totalAmountCents),
      row.ninegridSharePercent.toFixed(1),
      row.totvsSharePercent.toFixed(1),
      dependencyLabel(row.dependencySource),
      dependencyLevelLabel(row.dependencyLevel),
    ]),
    margin: { left: margin, right: margin },
    headStyles: { fillColor: [41, 128, 185], textColor: 255, fontSize: 9, fontStyle: 'bold' },
    bodyStyles: { fontSize: 8, textColor: 50 },
    alternateRowStyles: { fillColor: [245, 245, 245] },
  })

  const pageCount = (pdf as any).internal.getNumberOfPages()
  for (let i = 1; i <= pageCount; i++) {
    pdf.setPage(i)
    pdf.setFontSize(8)
    pdf.setTextColor(150, 150, 150)
    pdf.text(`Página ${i} de ${pageCount}`, pageWidth / 2, pageHeight - 10, { align: 'center' })
  }

  pdf.save(filename)
}

const SellerChannelDependencyReportPage: FC = () => {
  const { filters, setFilters, isReady } = useSellerReportFilters<SellerTicketAverageFilters>({
    ...createDefaultSellerReportDateRange(),
    source: 'all',
  })
  const [loading, setLoading] = useState(false)
  const [report, setReport] = useState<SellerTicketAverageReport | null>(null)

  const loadReport = useCallback(async () => {
    try {
      setLoading(true)
      const data = await loadSellerTicketAverageReport(filters)
      setReport(data)
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Erro ao carregar relatorio',
        text: error instanceof Error ? error.message : 'Erro desconhecido',
        confirmButtonText: 'OK',
      })
    } finally {
      setLoading(false)
    }
  }, [filters])

  useEffect(() => {
    if (isReady) {
      void loadReport()
    }
  }, [isReady, loadReport])

  const sourceOptions = useMemo(
    () => [
      { value: 'all' as const, label: 'Todas as fontes' },
      { value: 'ninegrid' as const, label: 'Ninegrid' },
      { value: 'totvs' as const, label: 'TOTVS' },
    ],
    []
  )

  const rows = useMemo(() => buildDependencyRows(report), [report])
  const riskyRows = useMemo(
    () => rows.filter((row) => row.dependencyLevel === 'critical' || row.dependencyLevel === 'high'),
    [rows]
  )
  const criticalRows = useMemo(
    () => rows.filter((row) => row.dependencyLevel === 'critical'),
    [rows]
  )
  const balancedRows = useMemo(
    () => rows.filter((row) => row.dependencyLevel === 'balanced'),
    [rows]
  )

  const onSourceChange = (source: SellerReportSourceFilter) => {
    setFilters((previous) => ({
      ...previous,
      source,
    }))
  }

  return (
    <>
      <PageTitle
        breadcrumbs={[
          {
            title: 'Dependência de Canal',
            path: '/seller/reports/channel-dependency',
            isActive: true,
          },
        ]}
      >
        Relatório de Dependência de Canal
      </PageTitle>

      <ReportShell
        title='Dependência de Canal por Vendedor'
        description='Identifica vendedores excessivamente concentrados em Ninegrid ou TOTVS, destacando risco operacional por falta de diversificação.'
        actions={
          <div className='d-flex gap-2'>
            <button className='btn btn-primary' onClick={loadReport} disabled={loading}>
              <i className='fas fa-sync-alt me-2'></i>
              {loading ? 'Carregando...' : 'Atualizar'}
            </button>
            <button className='btn btn-light-success' onClick={() => {
              const timestamp = new Date().toISOString().split('T')[0]
              exportToCSV(`Dependencia-Canal-${timestamp}.csv`, rows)
            }} disabled={loading || rows.length === 0} title='Baixar relatório em CSV'>
              <i className='fas fa-file-csv me-2'></i> CSV
            </button>
            <button className='btn btn-light-info' onClick={() => {
              const timestamp = new Date().toISOString().split('T')[0]
              exportToXLSX(`Dependencia-Canal-${timestamp}.xlsx`, rows)
            }} disabled={loading || rows.length === 0} title='Baixar relatório em XLSX'>
              <i className='fas fa-file-excel me-2'></i> XLSX
            </button>
            <button className='btn btn-light-danger' onClick={() => {
              const timestamp = new Date().toISOString().split('T')[0]
              exportToPDF(`Dependencia-Canal-${timestamp}.pdf`, rows, report)
            }} disabled={loading || rows.length === 0} title='Baixar relatório em PDF'>
              <i className='fas fa-file-pdf me-2'></i> PDF
            </button>
          </div>
        }
        filters={
          <div className='row g-3 align-items-end'>
            <div className='col-12 col-md-3'>
              <label className='form-label fw-semibold'>Data inicial</label>
              <input
                className='form-control'
                type='date'
                value={filters.startDate}
                onChange={(event) =>
                  setFilters((previous) => ({ ...previous, startDate: event.target.value }))
                }
              />
            </div>
            <div className='col-12 col-md-3'>
              <label className='form-label fw-semibold'>Data final</label>
              <input
                className='form-control'
                type='date'
                value={filters.finalDate}
                onChange={(event) =>
                  setFilters((previous) => ({ ...previous, finalDate: event.target.value }))
                }
              />
            </div>
            <div className='col-12 col-md-4'>
              <label className='form-label fw-semibold'>Fonte</label>
              <select
                className='form-select'
                value={filters.source}
                onChange={(event) => onSourceChange(event.target.value as SellerReportSourceFilter)}
              >
                {sourceOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className='col-12 col-md-2 d-grid'>
              <button className='btn btn-light-primary' onClick={loadReport} disabled={loading}>
                Buscar
              </button>
            </div>
          </div>
        }
      >
        <div className='row g-4 mb-4'>
          <div className='col-12 col-md-4'>
            <div className='card border-0 shadow-sm h-100'>
              <div className='card-body'>
                <div className='text-gray-600 fw-semibold mb-1'>Vendedores em risco</div>
                <div className='fs-2hx fw-bold text-warning'>
                  {riskyRows.length.toLocaleString('pt-BR')}
                </div>
                <div className='text-gray-600 mt-2'>Alta ou critica</div>
              </div>
            </div>
          </div>
          <div className='col-12 col-md-4'>
            <div className='card border-0 shadow-sm h-100'>
              <div className='card-body'>
                <div className='text-gray-600 fw-semibold mb-1'>Dependencia critica</div>
                <div className='fs-2hx fw-bold text-danger'>
                  {criticalRows.length.toLocaleString('pt-BR')}
                </div>
                <div className='text-gray-600 mt-2'>Acima de 95%</div>
              </div>
            </div>
          </div>
          <div className='col-12 col-md-4'>
            <div className='card border-0 shadow-sm h-100'>
              <div className='card-body'>
                <div className='text-gray-600 fw-semibold mb-1'>Vendedores balanceados</div>
                <div className='fs-2hx fw-bold text-success'>
                  {balancedRows.length.toLocaleString('pt-BR')}
                </div>
                <div className='text-gray-600 mt-2'>Abaixo de 70%</div>
              </div>
            </div>
          </div>
        </div>

        <div className='card border-0 shadow-sm'>
          <div className='card-header border-0 pt-5'>
            <div className='card-title d-flex flex-column'>
              <span className='fs-3 fw-bold text-dark'>Mapa de dependencia por vendedor</span>
              <span className='text-gray-600 mt-1'>Canal dominante, percentual de concentracao e receita exposta.</span>
            </div>
          </div>
          <div className='card-body pt-0'>
            {loading ? (
              <div className='d-flex justify-content-center align-items-center py-10'>
                <div className='spinner-border text-primary' role='status'>
                  <span className='visually-hidden'>Carregando...</span>
                </div>
              </div>
            ) : rows.length > 0 ? (
              <div className='table-responsive'>
                <table className='table align-middle table-row-dashed fs-6 gy-3'>
                  <thead>
                    <tr className='text-start text-gray-600 fw-bold fs-7 text-uppercase gs-0'>
                      <th>Vendedor</th>
                      <th className='text-end'>Pedidos</th>
                      <th className='text-end'>Faturamento</th>
                      <th className='text-end'>Ninegrid</th>
                      <th className='text-end'>TOTVS</th>
                      <th className='text-end'>Canal dominante</th>
                      <th className='text-end'>Nivel</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr key={row.sellerKey}>
                        <td>
                          <div className='fw-bold text-dark'>
                            <SellerIdentity label={row.sellerLabel} />
                          </div>
                        </td>
                        <td className='text-end fw-bold'>{row.totalOrders}</td>
                        <td className='text-end fw-bold text-success'>
                          {formatCurrency(row.totalAmountCents)}
                        </td>
                        <td className='text-end'>
                          <div className='fw-semibold text-primary'>
                            {formatCurrency(row.ninegridAmountCents)}
                          </div>
                          <div className='text-gray-600 fs-7'>
                            {percentFormatter.format(row.ninegridSharePercent)}%
                          </div>
                        </td>
                        <td className='text-end'>
                          <div className='fw-semibold text-warning'>
                            {formatCurrency(row.totvsAmountCents)}
                          </div>
                          <div className='text-gray-600 fs-7'>
                            {percentFormatter.format(row.totvsSharePercent)}%
                          </div>
                        </td>
                        <td className='text-end'>
                          {dependencyLabel(row.dependencySource)}
                        </td>
                        <td className='text-end'>
                          <span className={dependencyBadgeClass(row.dependencyLevel)}>
                            {dependencyLevelLabel(row.dependencyLevel)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className='alert alert-light-info mb-0'>
                Nenhum vendedor encontrado para os filtros selecionados.
              </div>
            )}
          </div>
        </div>
      </ReportShell>
    </>
  )
}

export { SellerChannelDependencyReportPage }
